/**
 * voyageExport.js
 *
 * FT-195: Voyage PDF Export using html2pdf.js
 *
 * Completely rewritten to use HTML-to-PDF conversion instead of jsPDF.
 * Renders voyage data using React components (VoyagePDFContent), then converts to PDF.
 * This gives pixel-perfect output matching the on-screen report.
 *
 * Key features:
 * - Uses html2pdf.js for reliable HTML-to-PDF conversion
 * - Supports BOTH old format (individual entries) AND new FT-161 format (grouped cards)
 * - Automatic page breaks with break-inside: avoid on each card
 * - No complex jsPDF coordinate math - just render HTML and convert
 */

import html2pdf from 'html2pdf.js';
import React from 'react';
import { createRoot } from 'react-dom/client';
import VoyagePDFContent from '../../components/tailwind/modules/operations/VoyagePDFContent';
import { formatDateInTimezone } from '../timezoneUtils';

// ============================================================================
// DATE RANGE UTILITIES
// ============================================================================

export const DATE_RANGE_PRESETS = {
  THIS_WEEK: 'this_week',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  LAST_3_MONTHS: 'last_3_months',
  CUSTOM: 'custom'
};

/**
 * Get date range from preset
 */
export function getDateRangeFromPreset(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case DATE_RANGE_PRESETS.THIS_WEEK: {
      const dayOfWeek = today.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(today);
      monday.setDate(today.getDate() + mondayOffset);
      return { from: monday, to: today };
    }

    case DATE_RANGE_PRESETS.THIS_MONTH: {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: firstDay, to: today };
    }

    case DATE_RANGE_PRESETS.LAST_MONTH: {
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: firstDay, to: lastDay };
    }

    case DATE_RANGE_PRESETS.LAST_3_MONTHS: {
      const firstDay = new Date(today.getFullYear(), today.getMonth() - 3, 1);
      return { from: firstDay, to: today };
    }

    default:
      return { from: today, to: today };
  }
}

// ============================================================================
// PDF OPTIONS
// ============================================================================

// Base PDF options - format will be set dynamically based on content height
const PDF_OPTIONS = {
  margin: 0, // Margins handled in the component
  filename: 'voyage-log.pdf',
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: {
    scale: 2,
    useCORS: true,
    letterRendering: true,
    logging: false
  },
  jsPDF: {
    unit: 'mm',
    orientation: 'portrait'
    // format will be set dynamically: [210, contentHeight]
  }
  // No pagebreak options - single continuous page
};

// ============================================================================
// RENDER HELPER
// ============================================================================

/**
 * Renders a React component to a temporary DOM element
 * Returns the element for html2pdf processing
 */
function renderToElement(Component, props) {
  return new Promise((resolve) => {
    // Create container
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);

    // Render React component
    const root = createRoot(container);
    root.render(<Component {...props} />);

    // Wait for render to complete
    setTimeout(() => {
      const element = container.querySelector('#voyage-pdf-content');
      resolve({ element, container, root });
    }, 100);
  });
}

/**
 * Cleanup after PDF generation
 */
function cleanup(container, root) {
  if (root) {
    root.unmount();
  }
  if (container && container.parentNode) {
    container.parentNode.removeChild(container);
  }
}

// ============================================================================
// SINGLE VOYAGE EXPORT
// ============================================================================

/**
 * Export a single voyage as PDF
 *
 * @param {Object} voyage - The voyage data
 * @param {Object} options - Export options
 * @param {string} options.vesselName - Name of the vessel
 * @returns {Promise<void>}
 */
export async function exportVoyagePDF(voyage, options = {}) {
  const { vesselName = 'Unknown Vessel', vessel = null } = options;

  // Generate filename
  const voyageNumber = voyage.voyageNumber || voyage.number || 'unknown';
  // FT-269: use the voyage's preferred (or vessel's) timezone for the date
  // segment, not UTC. Late-night departures previously rolled to the wrong
  // calendar day in the filename and the on-page heading.
  const tz = voyage.preferredTimezone
    || vessel?.timezone
    || vessel?.settings?.timezone
    || (typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'UTC')
    || 'UTC';
  // Prefer the explicit eventTimezone snapshotted at write time on the
  // departure event, falling back to the voyage/vessel tz.
  const departureEventTz = (voyage.unifiedLog || []).find(e => e?.milestoneType === 'let_go')?.eventTimezone || tz;
  const dateStr = voyage.departureTime
    ? formatDateInTimezone(voyage.departureTime, departureEventTz, { format: 'YYYY-MM-DD' })
    : formatDateInTimezone(new Date().toISOString(), tz, { format: 'YYYY-MM-DD' });
  const filename = `voyage-${voyageNumber}-${dateStr}.pdf`;

  let container, root;

  try {
    // Render React component to DOM
    const rendered = await renderToElement(VoyagePDFContent, { voyage, vesselName });
    container = rendered.container;
    root = rendered.root;

    if (!rendered.element) {
      throw new Error('Failed to render PDF content');
    }

    // Calculate content height in mm (element is rendered at 210mm width)
    // html2canvas scale is 2, so we need to account for that
    const elementHeight = rendered.element.offsetHeight;
    // Convert px to mm (assuming 96 DPI: 1mm = 3.7795px)
    const contentHeightMm = Math.ceil(elementHeight / 3.7795) + 10; // Add small margin

    // Configure options with dynamic page height
    const pdfOptions = {
      ...PDF_OPTIONS,
      filename,
      jsPDF: {
        ...PDF_OPTIONS.jsPDF,
        format: [210, contentHeightMm] // A4 width, dynamic height
      }
    };

    // Generate PDF
    await html2pdf()
      .set(pdfOptions)
      .from(rendered.element)
      .save();

    console.log(`[voyageExport] Successfully exported: ${filename} (height: ${contentHeightMm}mm)`);

  } catch (error) {
    console.error('[voyageExport] Export failed:', error);
    throw error;

  } finally {
    cleanup(container, root);
  }
}

// ============================================================================
// BATCH VOYAGE EXPORT
// ============================================================================

/**
 * Export multiple voyages as a single PDF
 *
 * @param {Array} voyages - Array of voyage data objects
 * @param {Object} options - Export options
 * @param {string} options.vesselName - Name of the vessel
 * @returns {Promise<void>}
 */
export async function exportVoyageBatchPDF(voyages, options = {}) {
  const { vesselName = 'Unknown Vessel', vessel = null } = options;

  if (!voyages || voyages.length === 0) {
    console.warn('[voyageExport] No voyages to export');
    return;
  }

  // For single voyage, use single export
  if (voyages.length === 1) {
    return exportVoyagePDF(voyages[0], options);
  }

  // Generate filename for batch.
  // FT-269: batch heading uses vessel tz (not UTC) so late-night exports
  // don't pick up tomorrow's date.
  const tz = vessel?.timezone
    || vessel?.settings?.timezone
    || (typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'UTC')
    || 'UTC';
  const dateStr = formatDateInTimezone(new Date().toISOString(), tz, { format: 'YYYY-MM-DD' });
  const filename = `voyage-batch-${voyages.length}-${dateStr}.pdf`;

  let container;

  try {
    // Create a container for all voyages
    container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    document.body.appendChild(container);

    // Render all voyages
    const batchContent = document.createElement('div');
    batchContent.id = 'voyage-pdf-content';
    batchContent.style.width = '210mm';
    batchContent.style.backgroundColor = '#f1f5f9';
    container.appendChild(batchContent);

    // Render each voyage
    for (let i = 0; i < voyages.length; i++) {
      const voyage = voyages[i];

      // Create voyage container
      const voyageContainer = document.createElement('div');
      if (i > 0) {
        voyageContainer.style.pageBreakBefore = 'always';
        voyageContainer.className = 'page-break-before';
      }
      batchContent.appendChild(voyageContainer);

      // Render React component for this voyage
      const voyageRoot = createRoot(voyageContainer);
      voyageRoot.render(<VoyagePDFContent voyage={voyage} vesselName={vesselName} />);

      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Wait for all renders to complete
    await new Promise(resolve => setTimeout(resolve, 200));

    // Configure options
    const pdfOptions = {
      ...PDF_OPTIONS,
      filename
    };

    // Generate PDF
    await html2pdf()
      .set(pdfOptions)
      .from(batchContent)
      .save();

    console.log(`[voyageExport] Successfully exported batch: ${filename}`);

  } catch (error) {
    console.error('[voyageExport] Batch export failed:', error);
    throw error;

  } finally {
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================================================

// Re-export as default for backward compatibility
export default {
  exportVoyagePDF,
  exportVoyageBatchPDF
};
